import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ArrowLeft, Check, Plus, Wand2, type LucideIcon } from "lucide-react";

/** Back arrow, step dots and a centred, calm column. The dialog's own close button sits top-right. */
export function StepShell({ total, current, onBack, children, footer }: { total: number; current: number; onBack?: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center px-4">
        <div className="w-10">
          {onBack && (
            <button type="button" onClick={onBack} aria-label="Back" className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-1 items-center justify-center gap-1.5" aria-label={`Step ${current + 1} of ${total}`}>
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={cn("h-1.5 rounded-full transition-all duration-300", i === current ? "w-6 bg-primary" : i < current ? "w-1.5 bg-primary/50" : "w-1.5 bg-muted-foreground/20")} />
          ))}
        </div>
        <div className="w-10" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4 sm:px-10">{children}</div>
      {footer && <div className="shrink-0 px-6 pb-6 pt-2 sm:px-10">{footer}</div>}
    </div>
  );
}

export function Question({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="w-full min-w-0 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <h2 className="mb-7 text-center text-2xl font-semibold tracking-tight">{title}</h2>
      {children}
    </div>
  );
}

/** One-line, full-width tap target. */
export function ChoiceRow({ active, onClick, icon: Icon, label, title, children }: { active: boolean; onClick: () => void; icon?: LucideIcon; label: string; title?: string; children?: React.ReactNode }) {
  return (
    <div className={cn("rounded-xl border transition-colors", active ? "border-success bg-success/5" : "border-border hover:border-success/40 hover:bg-muted/40")}>
      <button type="button" onClick={onClick} aria-pressed={active} title={title} className="flex h-14 w-full items-center gap-3 px-4 text-left">
        {Icon && <Icon className={cn("h-4 w-4 shrink-0", active ? "text-success" : "text-muted-foreground")} />}
        <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
        {active && <Check className="h-4 w-4 shrink-0 text-success" />}
      </button>
      {active && children && <div className="animate-in fade-in space-y-2 px-4 pb-4">{children}</div>}
    </div>
  );
}

/** Large square tile used for the goal picker. */
export function GoalTile({ icon: Icon, label, onClick, active }: { icon: LucideIcon; label: string; onClick: () => void; active?: boolean }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={cn("flex h-28 flex-col items-center justify-center gap-2.5 rounded-2xl border transition-all", active ? "border-success bg-success/5" : "border-border hover:-translate-y-0.5 hover:border-success/40 hover:shadow-sm")}>
      <Icon className="h-6 w-6 text-primary" />
      <span className="font-medium">{label}</span>
    </button>
  );
}

/** Tappable pills (multi-select) with an inline "+ Add". */
export function PillToggle({ options, value, onChange, addLabel = "Add", single, noAdd, className }: { options: { v: string; label: string }[]; value: string[]; onChange: (v: string[]) => void; addLabel?: string; single?: boolean; noAdd?: boolean; className?: string }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const all = [...options, ...value.filter((v) => !options.some((o) => o.v.toLowerCase() === v.toLowerCase())).map((v) => ({ v, label: v }))];
  const toggle = (v: string) => {
    const on = value.some((x) => x.toLowerCase() === v.toLowerCase());
    if (single) onChange(on ? [] : [v]);
    else onChange(on ? value.filter((x) => x.toLowerCase() !== v.toLowerCase()) : [...value, v]);
  };
  const commit = () => {
    const t = text.trim();
    if (t && !value.some((x) => x.toLowerCase() === t.toLowerCase())) onChange(single ? [t] : [...value, t]);
    setText(""); setAdding(false);
  };
  return (
    <div className={cn("flex flex-wrap justify-center gap-2", className)}>
      {all.map((o) => {
        const on = value.some((x) => x.toLowerCase() === o.v.toLowerCase());
        return (
          <button key={o.v} type="button" onClick={() => toggle(o.v)} aria-pressed={on}
            className={cn("inline-flex h-10 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors", on ? "border-success bg-success text-success-foreground" : "border-border bg-background hover:border-success/40")}>
            {on && <Check className="h-3.5 w-3.5" />}{o.label}
          </button>
        );
      })}
      {noAdd ? null : adding ? (
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setText(""); setAdding(false); } }}
          className="h-10 w-44 rounded-full border border-primary bg-background px-4 text-sm outline-none" placeholder="Type and press Enter" />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="inline-flex h-10 items-center gap-1 rounded-full border border-dashed px-4 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
          <Plus className="h-3.5 w-3.5" />{addLabel}
        </button>
      )}
    </div>
  );
}

const LINES = ["Reading your business profile", "Finding the right people", "Shaping your campaigns"];
export function BuildingState({ title = "Building your campaigns…", lines = LINES }: { title?: string; lines?: string[] } = {}) {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % lines.length), 1800); return () => clearInterval(t); }, [lines.length]);
  return (
    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-6 text-center">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-primary/15" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground"><Wand2 className="h-6 w-6" /></span>
      </div>
      <div>
        <p className="text-xl font-semibold tracking-tight">{title}</p>
        <p key={i} className="mt-2 animate-in fade-in text-sm text-muted-foreground duration-500">{lines[i % lines.length]}</p>
      </div>
    </div>
  );
}
